/**
 *
 * CARD CREATION LOGIC FOR ADS
 * THESE METHODS SHOULD REMAIN ENIRELY FUNCTIONAL, AND SEPARATE FROM VUE RENDERING
 *
 * AKA DONT USE INJECTS INSIDE HERE OTHERWISE ITS GONNA BE A BITCH TO TEST!
 *
 */

import { Brand, CardOrdering, TemplateSize } from "@fresh8/types/src";
import { BetData, Market } from "@fresh8/types/src/betData";
import { Preview } from "@fresh8/types/src/preview";
import MarketSupports from "../../lib/marketSupports";
import { computed, ComputedRef, inject, InjectionKey, provide, Ref } from "vue";
import { eventCardCreationMap, generalCardCreationMap } from "./cards";
import {
  additionalFeatureDefaults,
  AdditionalFeatures,
  EventGroup,
  MarketCard
} from "./types";
import { EventMarketGroups, Markets } from "./types";
import { Sport } from "@fresh8/types/src/language";
import {
  FTR_GROUP,
  MAIN_MARKET_GROUP,
  WDW_GROUP,
  WW_GROUP
} from "../../constants/market-types";
import { AnalyticsBet } from "@fresh8/types/src/analytics";

const DEFAULT_EVENT_CARD_ORDER: CardOrdering["event"] = [
  "promo",
  "mainMarket",
  "wdw",
  "ww",
  "insight",
  "list"
];

const marketSupportsMap = MarketSupports();

const MAIN_MARKET_SPORTS = [
  "Basketball",
  "Baseball",
  "American Football",
  "Ice Hockey"
];

export interface CreateCardSequence {
  cards: ComputedRef<MarketCard[]>;
  firstThreeMarketIds: ComputedRef<AnalyticsBet[]>;
}

export function createCardSequence(
  betData: Ref<BetData>,
  brand: Ref<Brand>,
  templateSize: Ref<TemplateSize>,
  preview: Ref<Preview>,
  features?: AdditionalFeatures
): CreateCardSequence {
  features = {
    ...additionalFeatureDefaults,
    ...features
  };

  const eventCardOrder = computed<CardOrdering["event"]>(
    () =>
      brand.value?.adTemplate?.cardOrdering?.event ?? DEFAULT_EVENT_CARD_ORDER
  );

  const generalCardOrder = computed<CardOrdering["general"]>(
    () => brand.value?.adTemplate?.cardOrdering?.general ?? ["events"]
  );

  const eventGroups = computed(() => {
    if (!generalCardOrder.value.includes("events")) return [];

    const eventArray = Object.values(betData.value?.events ?? {});

    if (!eventArray) return [];

    return eventArray?.map(sportEvent => {
      const marketIdGroups = createMarketIDGroups(
        sportEvent.markets,
        betData.value.markets,
        sportEvent.sport,
        eventCardOrder.value
      );

      const event = new EventGroup(sportEvent, marketIdGroups);

      for (const cardType of eventCardOrder.value) {
        const createCard = eventCardCreationMap[cardType];

        const cards = createCard(event, {
          betData: betData.value,
          brand: brand.value,
          size: templateSize.value,
          preview: preview.value,
          insightABTestingEnabled:
            features?.insightABTestingEnabled?.value ?? false
        });

        event.addCard(cards);
      }

      return event;
    });
  });

  const orderedEventsGroups = computed(() => {
    if (!eventGroups.value) return [];
    return orderEventsByDate(eventGroups.value);
  });

  const cardArrayFromEvents = computed(() => {
    if (!eventGroups.value) return [];

    let result: Array<MarketCard> = [];

    for (const evt of orderedEventsGroups.value) {
      result = result.concat(...evt.getCards());
    }

    return result;
  });

  const cardArrayWithGeneralCards = computed(() => {
    return generalCardOrder.value.flatMap(card => {
      if (card === "events") return cardArrayFromEvents.value;

      const createGeneralCard = generalCardCreationMap[card];

      return createGeneralCard(orderedEventsGroups.value, {
        betData: betData.value,
        brand: brand.value,
        size: templateSize.value,
        preview: preview.value,
        displayOfferCard: features?.displayOfferCard?.value
      });
    }) as Array<MarketCard>;
  });

  const firstThreeMarketIds = computed(() => {
    const result: AnalyticsBet[] = [];
    for (const card of cardArrayFromEvents.value) {
      const mIds = card.marketIds;

      if (mIds.length > 1) {
        for (const id of mIds) {
          const o: AnalyticsBet = {
            betID: id
          };
          if (card.optionIds) {
            o["betOptionID"] = card.optionIds[0];
          }

          result.push(o);
        }
      } else {
        const o: AnalyticsBet = {
          betID: mIds[0]
        };
        if (card.optionIds) {
          o["betOptionID"] = card.optionIds[0];
        }

        result.push(o);
      }

      if (result.length >= 3) {
        result.slice(0, 3);
        break;
      }
    }

    return result;
  });

  return { cards: cardArrayWithGeneralCards, firstThreeMarketIds };
}

/**
 * orders eventgroups by priority then date then if they have promo, see tests for examples
 */
export function orderEventsByDate(
  events: Array<EventGroup>
): Array<EventGroup> {
  // we use array.from to copy the array so .sort doesn't mutate the original array. (this will stop unneeded computedRef recalculation)
  return Array.from(events).sort((a, b) => {
    if (a.isPriority) return -1;

    if (b.isPriority) return 1;

    if (a.date < b.date) return -1;

    if (a.date > b.date) return 1;

    if (a.has("promo") && !b.has("promo")) return -1;

    if (!a.has("promo") && b.has("promo")) return 1;

    return 0;
  });
}

/**
 * Groups Marketids into structs based on their marketType
 */
function createMarketIDGroups(
  marketIds: string[],
  markets: Markets,
  sport: Sport,
  eventCardOrder: CardOrdering["event"]
): EventMarketGroups {
  const marketIdGroup = marketIds.reduce((groupAccumulator, currentID) => {
    const marketType = markets[currentID].marketType;
    const isPriorityMarket = markets[currentID].priorityMarket;
    let marketTypeGroup = marketSupportsMap[marketType];

    if (
      marketTypeGroup === FTR_GROUP &&
      !!groupAccumulator[MAIN_MARKET_GROUP]
    ) {
      marketTypeGroup = MAIN_MARKET_GROUP;
    }

    let accumulatorGroup: string[] = groupAccumulator[marketTypeGroup] || [];

    if (groupAccumulator[marketTypeGroup]) {
      // make sure the priority market is always first in the marketTypeGroup
      // this is used so we can target specific promo options via the campaigns.
      if (isPriorityMarket) {
        accumulatorGroup.unshift(currentID);
      } else {
        accumulatorGroup.push(currentID);
      }
    } else {
      accumulatorGroup = [currentID];
    }

    if (marketTypeGroup === FTR_GROUP) {
      if (
        MAIN_MARKET_SPORTS.includes(sport) &&
        accumulatorGroup.length > 1 &&
        eventCardOrder.includes(MAIN_MARKET_GROUP)
      ) {
        groupAccumulator[MAIN_MARKET_GROUP] = [...accumulatorGroup];
      } else {
        if (hasDrawOption(markets[accumulatorGroup[0]])) {
          groupAccumulator[WDW_GROUP] = [...accumulatorGroup];
        } else {
          groupAccumulator[WW_GROUP] = [...accumulatorGroup];
        }
      }
    }

    groupAccumulator[marketTypeGroup] = accumulatorGroup;

    return groupAccumulator;
  }, {} as EventMarketGroups);

  return Object.entries(marketIdGroup).reduce(
    (acc, [key, value]: [string, string[]]) => {
      if (key === FTR_GROUP) return acc;

      if (key === WW_GROUP || key === WDW_GROUP) {
        value = value.filter(v => !marketIdGroup.mainMarket?.includes(v));

        if (!value.length) return acc;
      }

      acc[key] = value;

      return acc;
    },
    {} as EventMarketGroups
  );
}

const hasDrawOption = (eventMarket: Market) => {
  return eventMarket.options.length > 2;
};

const injectionKey: InjectionKey<CreateCardSequence> = Symbol("card-creation");

export function provideMarketCards(marketCardContext: CreateCardSequence) {
  provide(injectionKey, marketCardContext);
}

export function injectMarketCards() {
  return inject(injectionKey, {
    cards: computed(() => []),
    firstThreeMarketIds: computed(() => [])
  });
}
